const FLAG_OPTIONS = Object.freeze([
    { key: 'g', label: 'g', title: '全局匹配' },
    { key: 'i', label: 'i', title: '忽略大小写' },
    { key: 'm', label: 'm', title: '多行模式，^ $ 匹配每行首尾' },
    { key: 's', label: 's', title: '点号 . 匹配换行' },
    { key: 'u', label: 'u', title: 'Unicode 模式' },
    { key: 'y', label: 'y', title: '粘性匹配，从 lastIndex 开始' }
])

const PRESETS = Object.freeze([
    { name: '邮箱', pattern: '[\\w.+-]+@[\\w-]+\\.[a-zA-Z]{2,}' },
    { name: '手机号', pattern: '1[3-9]\\d{9}' },
    { name: 'IPv4', pattern: '(?:\\d{1,3}\\.){3}\\d{1,3}' },
    { name: 'URL', pattern: 'https?://[^\\s]+' },
    { name: '中文', pattern: '[\\u4e00-\\u9fa5]+' },
    { name: '日期', pattern: '\\d{4}-\\d{2}-\\d{2}' },
    { name: '身份证', pattern: '\\d{17}[\\dXx]' },
    { name: '空白', pattern: '\\s+' }
])

const CHEATSHEET = Object.freeze([
    {
        name: '字符',
        items: [
            { token: '.', meaning: '任意单个字符（默认不含换行）' },
            { token: '\\d', meaning: '数字，等价 [0-9]' },
            { token: '\\D', meaning: '非数字' },
            { token: '\\w', meaning: '单词字符，等价 [A-Za-z0-9_]' },
            { token: '\\W', meaning: '非单词字符' },
            { token: '\\s', meaning: '空白，空格 / Tab / 换行等' },
            { token: '\\S', meaning: '非空白' },
            { token: '\\n', meaning: '换行' },
            { token: '\\t', meaning: '制表符 Tab' },
            { token: '\\', meaning: '转义后面的特殊字符，如 \\. \\( \\?' }
        ]
    },
    {
        name: '位置',
        items: [
            { token: '^', meaning: '行首；配合 m 标志匹配每行开头' },
            { token: '$', meaning: '行尾；配合 m 标志匹配每行结尾' },
            { token: '\\b', meaning: '单词边界' },
            { token: '\\B', meaning: '非单词边界' }
        ]
    },
    {
        name: '量词',
        items: [
            { token: '*', meaning: '前面的内容出现 0 次或多次' },
            { token: '+', meaning: '前面的内容出现 1 次或多次' },
            { token: '?', meaning: '前面的内容出现 0 次或 1 次' },
            { token: '{n}', meaning: '恰好 n 次' },
            { token: '{n,}', meaning: '至少 n 次' },
            { token: '{n,m}', meaning: 'n 到 m 次' },
            { token: '*? +? ??', meaning: '惰性匹配，尽量少匹配' }
        ]
    },
    {
        name: '分组',
        items: [
            { token: '(abc)', meaning: '捕获分组，可用 $1、$2 引用' },
            { token: '(?:abc)', meaning: '非捕获分组，只分组不编号' },
            { token: '(?<name>)', meaning: '命名捕获组' },
            { token: '|', meaning: '或，如 cat|dog' },
            { token: '[abc]', meaning: '字符集合，匹配其中任一字符' },
            { token: '[^abc]', meaning: '取反字符集合' },
            { token: '[a-z]', meaning: '范围，如小写字母、0-9' }
        ]
    },
    {
        name: '标志',
        items: [
            { token: 'g', meaning: '全局匹配，找出全部结果' },
            { token: 'i', meaning: '忽略大小写' },
            { token: 'm', meaning: '多行模式，^ $ 匹配每行首尾' },
            { token: 's', meaning: '点号 . 也匹配换行' },
            { token: 'u', meaning: 'Unicode 模式' },
            { token: 'y', meaning: '粘性匹配，从 lastIndex 开始' }
        ]
    }
])

const MAX_MATCHES = 2000

const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const compileRegex = (pattern, flags) => {
    if (!pattern) {
        return { ok: true, regex: null, error: '' }
    }
    try {
        return { ok: true, regex: new RegExp(pattern, flags), error: '' }
    } catch (error) {
        return { ok: false, regex: null, error: error.message }
    }
}

const toMatch = (execResult, index) => ({
    index,
    value: execResult[0],
    start: execResult.index,
    end: execResult.index + execResult[0].length,
    groups: execResult.slice(1).map((value, groupIndex) => ({
        index: groupIndex + 1,
        value: value == null ? '' : value
    })),
    named: execResult.groups || {}
})

const cloneRegex = (regex) => new RegExp(regex.source, regex.flags)

const collectMatches = (regex, text) => {
    if (!regex || text == null) {
        return { matches: [], truncated: false }
    }

    const local = cloneRegex(regex)
    if (!local.global) {
        const result = local.exec(text)
        return {
            matches: result ? [toMatch(result, 0)] : [],
            truncated: false
        }
    }

    const matches = []
    let result
    while ((result = local.exec(text)) !== null) {
        matches.push(toMatch(result, matches.length))
        if (result[0].length === 0) {
            local.lastIndex += 1
        }
        if (matches.length >= MAX_MATCHES) {
            return { matches, truncated: true }
        }
    }
    return { matches, truncated: false }
}

const applyReplace = (regex, text, replacement) => {
    if (!regex) {
        return { ok: true, value: text, error: '' }
    }
    try {
        return { ok: true, value: text.replace(cloneRegex(regex), replacement), error: '' }
    } catch (error) {
        return { ok: false, value: '', error: error.message }
    }
}

const highlightHtml = (text, ranges = []) => {
    if (!text) {
        return ''
    }
    const valid = ranges.filter((range) => range && range.start >= 0 && range.end >= range.start)
    if (!valid.length) {
        return escapeHtml(text)
    }

    const events = []
    valid.forEach((range) => {
        if (range.end === range.start) {
            events.push({ pos: range.start, kind: 0, range })
            return
        }
        events.push({ pos: range.start, kind: 1, range })
        events.push({ pos: range.end, kind: -1, range })
    })
    events.sort((a, b) => a.pos - b.pos || a.kind - b.kind)

    let html = ''
    let cursor = 0
    const active = []

    const wrap = (slice) => {
        if (!slice) {
            return ''
        }
        const escaped = escapeHtml(slice)
        if (!active.length) {
            return escaped
        }
        const className = [...new Set(active.map((item) => item.className))].join(' ')
        return `<mark class="${className}">${escaped}</mark>`
    }

    events.forEach((event) => {
        if (event.pos > cursor) {
            html += wrap(text.slice(cursor, event.pos))
            cursor = event.pos
        }
        if (event.kind === 1) {
            active.push(event.range)
            return
        }
        if (event.kind === -1) {
            const index = active.indexOf(event.range)
            if (index >= 0) {
                active.splice(index, 1)
            }
            return
        }
        html += `<mark class="${event.range.className}">∅</mark>`
    })
    html += wrap(text.slice(cursor))
    return html
}

const toHighlightRanges = (matches, className, activeIndex = -1) =>
    matches.map((match) => ({
        start: match.start,
        end: match.end,
        className: match.index === activeIndex ? `${className} is-active` : className
    }))

const collectLiteralHits = (text, query, max = MAX_MATCHES) => {
    if (!text || !query) {
        return []
    }
    const haystack = text.toLowerCase()
    const needle = query.toLowerCase()
    const hits = []
    let from = 0
    while (from <= haystack.length - needle.length) {
        const start = haystack.indexOf(needle, from)
        if (start < 0) {
            break
        }
        hits.push({
            index: hits.length,
            start,
            end: start + query.length,
            value: text.slice(start, start + query.length)
        })
        from = start + (needle.length || 1)
        if (hits.length >= max) {
            break
        }
    }
    return hits
}

const joinMatches = (matches, separator = '\n') =>
    matches.map((match) => match.value).join(separator)

export {
    FLAG_OPTIONS,
    PRESETS,
    CHEATSHEET,
    MAX_MATCHES,
    compileRegex,
    collectMatches,
    applyReplace,
    highlightHtml,
    toHighlightRanges,
    collectLiteralHits,
    joinMatches
}